import { Expose } from 'class-transformer';
import type BasketProduct from './BasketProduct';
import type DerivedDiscount from '../types/DerivedDiscount';
import type Discount from '@/types/Discount';
import type AppliedReward from '../types/AppliedReward';
import type DeliveryAddress from '../types/DeliveryAddress';
import type RestaurantCalendar from '../types/RestaurantCalendar';
import type BasketCoupon from '../types/BasketCoupon';
import type BasketFee from '../types/BasketFee';
import type Location from './Location';
import { DateTime } from "luxon";
import type CalendarRange from '@/types/CalendarRange';
import type CateringOrderInterface from '@/types/CateringOrder';
import OrdersService from '../services/Orders';
import type OrderDetail from './OrderDetails';

export default class Basket {
  @Expose() appliedrewards: AppliedReward[] = [];
  @Expose() contactnumber: string = "";
  @Expose() coupon: BasketCoupon = {} as BasketCoupon;
  @Expose() coupons: BasketCoupon[] = [] as BasketCoupon[];
  @Expose() coupondiscount: number = 0;
  @Expose() customerhandoffcharge: number = 0;
  // customfields : []
  @Expose() deliveryaddress: DeliveryAddress = {} as DeliveryAddress;
  @Expose() deliverymode: string = "";
  @Expose() deriveddiscounts: DerivedDiscount[] = [];
  @Expose() discount: number = 0;
  @Expose() discounts: Discount[] = [];
  @Expose() discountmode: string = "";
  // donations
  @Expose() earliestreadytime: string = "";
  @Expose() fees: BasketFee[] = [] as BasketFee[];
  @Expose() id: string = "";
  @Expose() leadtimeestimateminutes: number = 7;
  @Expose() loyaltymode: string = "";
  @Expose() mode: string = "";
  @Expose() products: BasketProduct[] = [];
  @Expose() salestax: number = 0;
  @Expose() subtotal: number = 0;
  // taxes []
  @Expose() timemode: string = "";
  @Expose() timewanted: string = "";
  @Expose() tax: number = 0;
  @Expose() tip: number = 0;
  @Expose() total: number = 0;
  @Expose() totaldonations: number = 0;
  @Expose() totalfees: number = 0;
  @Expose() vendorid: number = 0;
  @Expose() message: string = "";
  @Expose() catering_order_id: string = "";
  @Expose() catering_order: CateringOrderInterface = {} as CateringOrderInterface;
  @Expose() sourceorderguid: string = "";

  constructor(
    appliedrewards: AppliedReward[],
    contactnumber: string,
    coupon: BasketCoupon,
    coupondiscount: number,
    customerhandoffcharge: number,
    deliverymode: string,
    deriveddiscounts: DerivedDiscount[],
    discount: number,
    discounts: Discount[],
    discountmode: string,
    earliestreadytime: string,
    fees: BasketFee[],
    id: string,
    leadtimeestimateminutes: number,
    loyaltymode: string,
    mode: string,
    products: BasketProduct[],
    salestax: number,
    subtotal: number,
    timemode: string,
    timewanted: string,
    tax: number,
    tip: number,
    total: number,
    totaldonations: number,
    totalfees: number,
    vendorid: number,
    message: string,
    catering_order_id: string,
    catering_order: CateringOrderInterface,
    sourceorderguid: string,
  ) {
    this.appliedrewards = appliedrewards;
    this.contactnumber = contactnumber;
    this.coupon = coupon;
    this.coupondiscount = coupondiscount;
    this.customerhandoffcharge = customerhandoffcharge;
    this.deliverymode = deliverymode;
    this.deriveddiscounts = deriveddiscounts;
    this.discount = discount;
    this.discounts = discounts;
    this.discountmode = discountmode;
    this.earliestreadytime = earliestreadytime;
    this.fees = fees;
    this.id = id;
    this.leadtimeestimateminutes = leadtimeestimateminutes;
    this.loyaltymode = loyaltymode;
    this.mode = mode;
    this.products = products;
    this.salestax = salestax;
    this.subtotal = subtotal;
    this.timemode = timemode;
    this.timewanted = timewanted;
    this.tax = tax;
    this.tip = tip;
    this.total = total;
    this.totaldonations = totaldonations;
    this.totalfees = totalfees;
    this.vendorid = vendorid;
    this.message = message;
    this.catering_order_id = catering_order_id;
    this.catering_order = catering_order;
    this.sourceorderguid = sourceorderguid;
  } 

  friendlyHandOffMode = () => {
    switch(this.deliverymode) {
      case 'drivethru':
        return 'Drive-Thru'
        break;
      case 'curbside':
        return 'Grab & Go'
        break;
      case 'dispatch':
        return 'Delivery'
        break;
      case 'pickup':
        return 'Pick-up'
        break;
      default:
        return 'Pick-Up'
    }
  }

  manuallyAppliedRewards = ():DerivedDiscount[] => {
    if (this.deriveddiscounts && this.deriveddiscounts.length > 0) {
      return this.deriveddiscounts.filter((discount) => {
        return discount.type == 'Loyalty'
      })
    } else {
      return [] as DerivedDiscount[];
    }
  }

  appliedCoupon = ():BasketCoupon => {
    if (this.coupon && this.coupon.couponcode) {
      return this.coupon;
    } else {
      return {} as BasketCoupon;
    }
  }

  hasCouponsApplied = ():boolean => {
    return this.coupons && this.coupons.length > 0;
  }

  hasRewardApplied = (identifier: string): boolean => {
    if (this.coupons != null && this.coupons.length > 0) {
      let matchedCoupon = this.coupons.find(coupon => coupon.couponcode == identifier);
      return matchedCoupon != null;
    } else {
      return false
    }
  }

  // Takes a validation basket object and merges specific attributes onto the normal basket
  mergeValidationBasket = (validationBasket: Basket):Basket => {
     const updatedBasket = this;
      if (updatedBasket && validationBasket) {
          // updatedBasket.fees = basket.fees
          updatedBasket.salestax = validationBasket.tax
          // updatedBasket.taxes = basket.taxes
          updatedBasket.deriveddiscounts = validationBasket.deriveddiscounts;
          updatedBasket.subtotal = validationBasket.subtotal
          updatedBasket.total = validationBasket.total
          updatedBasket.totalfees = validationBasket.totalfees
      }
      return updatedBasket;
  }

  // Give an option bpid to filter out a specific product from the amount fed
  basketFeeds = (guestHungerArray: number[], totalGuests: number, bpid?: string):number => {
    let count = 0;
    let lightFeeds = 0;
    let regularFeeds = 0;
    let hungryFeeds = 0;
    let lightPct = 0;
    let regularPct = 0;
    let hungryPct = 0;

    if (totalGuests && totalGuests > 0 ) {
      let light = guestHungerArray.at(0);
      let typical = guestHungerArray.at(1);
      let hungry = guestHungerArray.at(2);

      if ((light && light > 0) || (typical && typical > 0) || (hungry && hungry > 0)) {
        lightPct = light!/totalGuests;
        regularPct = typical!/totalGuests;
        hungryPct = hungry!/totalGuests;
      } else {
        regularPct = 1;
      }
    } else {
      regularPct = 1;
    }

    if (this.products) {
      this.products.forEach((prod) => {
        if (!bpid || prod.id.toString() != bpid) {
          if (prod.feeds) {
            // let feedsLight = prod.feeds[0] * lightPct;
            // let feedsRegular = prod.feeds[1] * regularPct;
            // let feedsHeavy = prod.feeds[2] * heavyPct;
            // let feedsAmount = Math.round(feedsLight + feedsRegular + feedsHeavy);
            lightFeeds += (prod.feeds.at(0)!) * prod.quantity;
            regularFeeds += (prod.feeds.at(1)!) * prod.quantity;
            hungryFeeds += (prod.feeds.at(2)!) * prod.quantity;
            // count += feedsAmounts * prod.quantity;
          }
        }
      })

      let feedsLight = lightFeeds * lightPct;
      let feedsRegular = regularFeeds * regularPct;
      let feedsHungry = hungryFeeds * hungryPct;
      let feedsAmount = Math.round(feedsLight + feedsRegular + feedsHungry);

      count += feedsAmount;
    }
    
    return count;
  }

  // Given the location; will return the next earliest ready time padded to 15
  // Accounting for if the order needs moved to the next date
  calculatedEarliestReadyTime(location: Location): DateTime {
    let calendar = location.catering_calendar;
    let ert = DateTime.fromFormat(this.earliestreadytime, "yyyyMMdd HH:mm");
    let cErt = this.roundDateTime(ert); // round the time to 15 minutes

    // Check the updated value is within the restaurant open hours for the date
    var dayOfWeek = cErt.toFormat('EEE');

    let cal = calendar.find((cal: RestaurantCalendar) => {
      return cal.type == 'business'
    })

    // Grab the calendar for the actual handoff mode instead
    if (this.deliverymode != "pickup" && this.deliverymode != null) {
      // Currently only supporting dispatch and pickup
      let newCal = calendar.find((cal: RestaurantCalendar) => {
        return cal.type == 'dispatch';
      })
      if (newCal) {
        cal = newCal;
      }
      // Should grab by Delivery but the string needs manipulated
    }

    const restaurantRange = cal!.ranges.filter((range: CalendarRange) => {
      return range.weekday == dayOfWeek;
    })

    if (restaurantRange != null) {
      var earliestOpenTime = location.getOpeningTimeForDate(cErt, this.deliverymode)
      var latestOpenTime = location.getClosingTimeForDate(cErt, this.deliverymode)

      if (earliestOpenTime && latestOpenTime) {
        if (cErt >= latestOpenTime.minus({minutes:15})) {
          // Then we can't allow this so default to earliest open time tomorrow
          // Get the opening time for tomorrow
          var earliestOpenTimeTomorrow = location.getOpeningTimeForDate(cErt.plus({days:1}), this.deliverymode)
          if (earliestOpenTimeTomorrow != null) {
            cErt = earliestOpenTimeTomorrow
          } else {
            // We have no recourse
          }
        }
      } else {
        // We have no recourse
      }
    }

    return cErt
  } 

  roundDateTime = (dt: DateTime): DateTime => {
    let newDateTime = DateTime.fromJSDate(dt.startOf('day').toJSDate()); // Copy the date to return the copy
    let hour = dt.hour
    let mins = dt.minute

    if (mins > 45) {
        hour += 1;
        mins = 0;
    } else if (mins > 30) {
        mins = 45
    } else if (mins > 15) {
        mins = 30
    } else if (mins > 0) {
        mins = 15
    }

    return newDateTime.plus({hours: hour, minutes: mins})
  }

  // Given a requested time and location (for calendar lookup), will give an approximate bool on whether the order
  // Can be prepared by then based on store calendar and estimatedleadtime
  canBeMadeByTime = (requestedTime: DateTime, location: Location, handoffMode: string): boolean => {
    let canBeMade = false;
    let calendar = location.catering_calendar;
    let now = DateTime.now();
    let deliveryMode = handoffMode != "delivery" ? 'pickup' : 'dispatch';
    let calculatedMakeTime = now.plus({minutes: this.leadtimeestimateminutes})
    
    var dayOfWeek = requestedTime.toFormat('EEE'); // We check the calendar for the day the user is requesting

    let cal = calendar.find((cal: RestaurantCalendar) => {
      return cal.type == 'business'
    })

    // Grab the calendar for the actual handoff mode instead
    // Should grab by delivery but the string needs manipulated
    if (deliveryMode != "pickup") {
      // Currently only supporting dispatch and pickup
      let newCal = calendar.find((cal: RestaurantCalendar) => {
        return cal.type == 'dispatch';
      })
      if (newCal) {
        cal = newCal;
      }
    }

    const restaurantRange = cal!.ranges.filter((range: CalendarRange) => {
      return range.weekday == dayOfWeek;
    })

    if (restaurantRange != null) {
      var earliestOpenTime = location.getOpeningTimeForDate(requestedTime, deliveryMode)
      var latestOpenTime = location.getClosingTimeForDate(requestedTime, deliveryMode)

      // First check that the leadtime fits into the selected day
      if (calculatedMakeTime < latestOpenTime!) {
        canBeMade = calculatedMakeTime < requestedTime;
      } else {
        // The lead time minutes bump this order order out past closing time on the day it is requested;
        // so it can't be made regardless of what time the requestedTime was
        canBeMade = false;
      }
    } else {
      // Something failed; probably with the location calendar so we can't provide a good guess
      // Just return true and let the validation on checkout pick up the slack
      canBeMade = true;
    }

    return canBeMade;
  }

  sourceOrder = (): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (this.sourceorderguid) {
        OrdersService.getOrder(this.sourceorderguid).then((order) => {
          resolve(order);
        }).catch((error: Error) => {
          // Got an error
          reject(error);
        })
      } else {
        // Resolve nothing since it didn't technically fail; it just has no related order
        resolve(null);
      }
    })
  }

  hasCateringProducts = (): boolean => {
    if (!this.products || this.products.length === 0) {
      return false;
    }

    return this.products.some((product) => {
      if (!product) {
        return false;
      }

      if (typeof product.isCatering === 'function') {
        try {
          return product.isCatering();
        } catch (_error) {
          return false;
        }
      }

      return false;
    });
  }
}